import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { i18n, getProductCodeFromOffer, getSeriousDiseaseKeysForOffer, getDiseaseCategoryKeysForOffer, setGlobalLoading, useAppDispatch } from 'src/common';
import { colors } from 'src/theme';
import { BackLink, Button, Flex, Text, ArrowLeftIcon, RadioButtonGroup, TermsCheckbox } from 'src/components';
import { Card } from './card';

const ConfirmationBox = styled.div({
  backgroundColor: '#F2EAF7',
  border: '1px solid #F2F2F2',
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const DiseaseList = styled.ul({
  fontSize: 16,
  lineHeight: '22px',
  color: colors.black,
  fontFamily: '"Red Hat Display", sans-serif',
  fontWeight: 400,
  margin: 0,
  paddingLeft: 20,
  listStyleType: 'disc',

  '& li': {
    marginBottom: 4,
  },
});

/** Wrapper to override TermsCheckbox styling on this page only: no background, smaller text */
const TermsCheckboxBare = styled.div({
  '& > div': {
    backgroundColor: 'transparent',
    padding: 0,
  },
  /* Text renders as styled divs with a class; [class] gives higher specificity than Emotion's typography */
  '& > div div[class]': {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: 400,
  },
});

export interface HealthQuestionnaireStoredState {
  seriousDiseaseValue: string;
  selectedDiseases: string[];
  isConfirmed: boolean;
}

export interface HealthQuestionnaireCardProps {
  onClose: () => void;
  /** Path for the back (Nazad) link; when provided, BackLink is used instead of button. */
  backPath?: string;
  /** Called when user confirms; receives current questionnaire state so parent can persist (e.g. by productCode). */
  onConfirm: (questionnaireState?: HealthQuestionnaireStoredState) => void;
  /** Current offer (e.g. from selection). Used to resolve per-offer questionnaire keys; fallback keys used when absent or not configured. */
  offer?: { productCode?: string } | null;
  /** When true, confirm button is disabled (e.g. waiting for session/soId). */
  confirmDisabled?: boolean;
  /** Restored state when returning from potvrda (keyed by productCode); new offer = no data = empty form. */
  initialValues?: Partial<HealthQuestionnaireStoredState>;
  className?: string;
}

export const HealthQuestionnaireCard: React.FC<HealthQuestionnaireCardProps> = ({
  onClose,
  backPath,
  onConfirm,
  offer,
  confirmDisabled = false,
  initialValues,
  className,
}) => {
  const [seriousDiseaseValue, setSeriousDiseaseValue] = useState<string>(initialValues?.seriousDiseaseValue ?? 'no');
  const [selectedDiseases, setSelectedDiseases] = useState<string[]>(initialValues?.selectedDiseases ?? []);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(initialValues?.isConfirmed ?? false);
  const dispatch = useAppDispatch();
  const productCode = getProductCodeFromOffer(offer ?? undefined);
  const seriousDiseaseKeys = getSeriousDiseaseKeysForOffer(productCode);
  const diseaseCategoryKeys = getDiseaseCategoryKeysForOffer(productCode);

  useEffect(() => {
    // console.log('[HealthQuestionnaire card] productCode from constants:', productCode ?? '(none)');
    // console.log('[HealthQuestionnaire card] seriousDiseaseKeys:', seriousDiseaseKeys.length, seriousDiseaseKeys);
    // console.log('[HealthQuestionnaire card] diseaseCategoryKeys:', diseaseCategoryKeys.length, diseaseCategoryKeys);
  }, [productCode, seriousDiseaseKeys, diseaseCategoryKeys]);

  const yesNoOptions = [
    { value: 'yes', label: i18n.t('pages.healthQuestionnaire.yes') },
    { value: 'no', label: i18n.t('pages.healthQuestionnaire.no') },
  ];

  const handleDiseaseToggle = (key: string) => {
    if (selectedDiseases.includes(key)) {
      setSelectedDiseases(selectedDiseases.filter(k => k !== key));
    } else {
      setSelectedDiseases([...selectedDiseases, key]);
    }
  };

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm({ seriousDiseaseValue, selectedDiseases, isConfirmed });
    } else {
      dispatch(setGlobalLoading(false));
    }
  };

  return (
    <Card radius={12} padding={20} className={className}>
      <Flex vertical gap={32}>
        {/* Header */}
        <Text type="small" bold style={{ textTransform: 'uppercase', letterSpacing: '1.2px', fontSize: 12 }}>
          {i18n.t('pages.healthQuestionnaire.title')}
        </Text>

        {/* Section 1: Serious Diseases */}
        <Flex vertical gap={16}>
          <Text type="large" bold>
            {i18n.t('pages.healthQuestionnaire.question1')}
          </Text>

          <DiseaseList>
            {seriousDiseaseKeys.map((key) => (
              <li key={key}>{i18n.t(`pages.healthQuestionnaire.${key}`)}</li>
            ))}
          </DiseaseList>

          <RadioButtonGroup
            options={yesNoOptions}
            value={seriousDiseaseValue}
            onChange={setSeriousDiseaseValue}
          />
        </Flex>

        {/* Section 2: Disease Categories */}
        <Flex vertical gap={16}>
          <Text type="large" bold>
            {i18n.t('pages.healthQuestionnaire.question2')}
          </Text>

          <Flex vertical gap={8}>
            {diseaseCategoryKeys.map((key) => (
              <TermsCheckboxBare key={key}>
                <TermsCheckbox
                  checked={selectedDiseases.includes(key)}
                  onChange={() => handleDiseaseToggle(key)}
                  title={i18n.t(`pages.healthQuestionnaire.${key}`)}
                  description=""
                  error=""
                  errorTitle=""
                  errorDescription=""
                  requestCallButtonText=""
                />
              </TermsCheckboxBare>
            ))}
          </Flex>
        </Flex>

        {/* Confirmation Checkbox */}
        <ConfirmationBox>
          <Text type="base" bold style={{ marginBottom: 8 }}>
            {i18n.t('pages.healthQuestionnaire.confirmationTitle')}
          </Text>
          <TermsCheckboxBare>
            <TermsCheckbox
              checked={isConfirmed}
              onChange={() => setIsConfirmed(!isConfirmed)}
              title={i18n.t('pages.healthQuestionnaire.confirmationText')}
              description=""
              error=""
              errorTitle=""
              errorDescription=""
              requestCallButtonText=""
            />
          </TermsCheckboxBare>
        </ConfirmationBox>

        {/* Buttons */}
        <Flex justify="space-between" align="center" gap={8}>
          {backPath != null ? (
            <BackLink text={i18n.t('pages.healthQuestionnaire.backButton')} path={backPath} />
          ) : (
            <Button
              btnType="outlineBlack"
              size="large"
              text={i18n.t('pages.healthQuestionnaire.backButton')}
              onClick={onClose}
              iconPosition="left"
            >
              <ArrowLeftIcon color={colors.black} />
            </Button>
          )}
          <Button
            btnType="primary"
            size="large"
            text={i18n.t('pages.healthQuestionnaire.confirmButton')}
            onClick={handleConfirm}
            disabled={!isConfirmed || confirmDisabled}
            style={{ minWidth: 178 }}
          />
        </Flex>
      </Flex>
    </Card>
  );
};